import React, { useEffect, useRef, useState } from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../utils/TylerState';
import AccountsManager from './forecast/AccountsManager';
import CashFlowsManager from './forecast/CashFlowsManager';
import FXManager from './forecast/FXManager';
import ForecastResults from './forecast/ForecastResults';
import StructuredData from './StructuredData';
import { ForecastScenario, CURRENCIES, TimelineHorizon, ValuationMode, ViewMode, resolveCategories } from '../types/personalForecast';
import personalForecastService from '../services/personal-forecast-service';
import '../styles/PersonalForecast.css';

type Section = 'accounts' | 'cashFlows' | 'fxRates' | 'forecast' | 'settings';

const SECTION_LABELS: Record<Section, string> = {
  accounts: 'What I Own & Owe',
  cashFlows: 'Cash Flows',
  fxRates: 'FX Rates',
  forecast: 'Forecast',
  settings: 'Settings',
};

/**
 * Which copy should load: this tab's unsaved draft, or the saved one? The draft used to win
 * outright, which meant a copy saved anywhere else stayed invisible for as long as the tab was
 * open, however often it was refreshed. The draft holds its own now only while the saved copy is
 * still the one it was built on.
 */
export const draftStillApplies = (
  draftBaseUpdatedAt: string | undefined,
  savedUpdatedAt: string | undefined,
): boolean => (savedUpdatedAt || '') === (draftBaseUpdatedAt || '');

const PersonalForecast: React.FC = () => {
  useTylerStateUpdate();
  const forecast = TylerState.forecast;
  const activeSection = forecast.activeSection as Section;
  const settings = forecast.settings;

  const [saveStatus, setSaveStatus] = useState<string>('');
  // A draft that lost to a newer saved copy, kept so the user can still choose it.
  const [shelvedDraft, setShelvedDraft] = useState<any>(null);
  // The updatedAt of the copy this tab started from, so a change made elsewhere is detectable.
  const baseUpdatedAt = useRef<string>('');

  // Unsaved edits are kept in this tab so a refresh (or a dev-server reload) does not lose them.
  const DRAFT_KEY = 'larryshelp_forecast_draft';
  useEffect(() => {
    if (!forecast.isDirty) return;
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
        scenarioId: forecast.scenarioId,
        scenarioName: forecast.scenarioName,
        accounts: forecast.accounts,
        cashFlows: forecast.cashFlows,
        fxRates: forecast.fxRates,
        settings: forecast.settings,
        // Not when the draft was written - which is always "now" while you type - but which saved
        // copy it was built on. That is what tells us whether the record moved under us.
        baseUpdatedAt: baseUpdatedAt.current,
      }));
    } catch {
      // Storage unavailable (private mode, quota): the page still works, just without the safety net.
    }
  });
  const clearDraft = () => { try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  // Load the most recently saved forecast. Rows from the old wizard are skipped, not loaded.
  useEffect(() => {
    const applyScenario = (scenario: ForecastScenario) => {
      baseUpdatedAt.current = scenario.updatedAt || '';
      TylerState.forecast.scenarioId = scenario.id || null;
      TylerState.forecast.scenarioName = scenario.name;
      TylerState.forecast.accounts = scenario.accounts || [];
      TylerState.forecast.cashFlows = scenario.cashFlows || [];
      TylerState.forecast.fxRates = scenario.fxRates || [];
      Object.assign(TylerState.forecast.settings, scenario.settings || {});
      TylerState.forecast.isDirty = false;
      updateReact();
    };
    const isOldWizardFormat = (scenario: ForecastScenario) => (scenario as any).income !== undefined;

    const applyDraft = (draft: any) => {
      baseUpdatedAt.current = draft.baseUpdatedAt || '';
      TylerState.forecast.scenarioId = draft.scenarioId || null;
      TylerState.forecast.scenarioName = draft.scenarioName || 'My Forecast';
      TylerState.forecast.accounts = draft.accounts || [];
      TylerState.forecast.cashFlows = draft.cashFlows || [];
      TylerState.forecast.fxRates = draft.fxRates || [];
      Object.assign(TylerState.forecast.settings, draft.settings || {});
      TylerState.forecast.isDirty = true;
      updateReact();
    };

    const readDraft = () => {
      try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    /**
     * A draft kept this tab's unsaved edits, and it used to win outright - which meant a copy saved
     * anywhere else stayed invisible for as long as the tab was open, however often it was
     * refreshed. Now the draft only wins while the saved copy is the one it was built on; if that
     * copy has moved on, the saved version loads and the draft is offered rather than imposed.
     */
    const load = async () => {
      const draft = readDraft();
      let saved: ForecastScenario[] = [];
      try {
        saved = await personalForecastService.getUserForecasts();
      } catch (error) {
        console.error('Failed to load forecast:', error);
      }
      const latest = saved.find(scenario => !isOldWizardFormat(scenario))
        || personalForecastService.getFromLocalStorage().find(scenario => !isOldWizardFormat(scenario));

      if (draft) {
        if (!latest || draftStillApplies(draft.baseUpdatedAt, latest.updatedAt)) {
          applyDraft(draft);
          setSaveStatus('Restored unsaved changes from before the page reloaded.');
          return;
        }
        if (latest) {
          applyScenario(latest);
          setShelvedDraft(draft);
          setSaveStatus('');
          return;
        }
      }
      if (latest) applyScenario(latest);
    };
    load();
  }, []);

  const setSection = (s: Section) => {
    TylerState.forecast.activeSection = s;
    updateReact();
  };

  const saveScenario = async () => {
    const scenario: ForecastScenario = {
      id: forecast.scenarioId || '',
      name: forecast.scenarioName,
      accounts: forecast.accounts,
      cashFlows: forecast.cashFlows,
      fxRates: forecast.fxRates,
      settings: forecast.settings,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (!TylerState.user) {
      personalForecastService.saveToLocalStorage({ ...scenario, id: scenario.id || 'local' });
      TylerState.forecast.isDirty = false;
      clearDraft();
      setSaveStatus('Saved on this device only. Sign in to keep it in your account.');
      updateReact();
      return;
    }
    try {
      if (forecast.scenarioId) {
        await personalForecastService.updateForecast(forecast.scenarioId, scenario);
      } else {
        TylerState.forecast.scenarioId = await personalForecastService.saveForecast(scenario);
      }
      TylerState.forecast.isDirty = false;
      clearDraft();
      setSaveStatus(`Saved ${new Date().toLocaleTimeString()}`);
      updateReact();
    } catch (error) {
      console.error('Failed to save forecast:', error);
      personalForecastService.saveToLocalStorage(scenario);
      setSaveStatus('Could not reach the server. Kept a copy on this device; try again shortly.');
      updateReact();
    }
  };

  const updateSetting = (field: string, value: any) => {
    (forecast.settings as any)[field] = value;
    forecast.isDirty = true;
    updateReact();
  };

  const categories = resolveCategories(settings, forecast.cashFlows);

  /** How many cash flow items would be left pointing at nothing if this category went. */
  const usageCount = (name: string) => forecast.cashFlows.filter(cf => cf.category === name).length;

  /**
   * Renaming carries the items with it. Without that, every item using the old name would be
   * orphaned the moment you corrected a typo - which is the opposite of what renaming means.
   */
  const renameCategory = (index: number, next: string) => {
    const before = categories[index];
    const list = [...categories];
    list[index] = next;
    forecast.cashFlows.forEach(cf => { if (cf.category === before) cf.category = next; });
    updateSetting('categories', list);
  };

  /** Deleting is the one that can orphan items, so it says how many and asks first. */
  const removeCategory = (index: number) => {
    const name = categories[index];
    const inUse = usageCount(name);
    if (inUse > 0 && !window.confirm(
      `${inUse} cash flow item${inUse === 1 ? '' : 's'} still use "${name}".\n\n` +
      'Deleting the category leaves them pointing at nothing. They will be flagged on the Cash ' +
      'Flows tab until you give each one a category.\n\nDelete it anyway?'
    )) return;
    updateSetting('categories', categories.filter((_, j) => j !== index));
  };

  const horizonYears = settings.horizon === 'custom'
    ? Math.max(1, Math.round((settings.customMonths || 60) / 12))
    : ({ '1yr': 1, '5yr': 5, '10yr': 10, '20yr': 20, '30yr': 30 } as Record<string, number>)[settings.horizon] || 5;

  const visibleSections: Section[] = settings.showFX
    ? ['accounts', 'cashFlows', 'fxRates', 'forecast', 'settings']
    : ['accounts', 'cashFlows', 'forecast', 'settings'];

  return (
    <div className="personal-forecast">
      <StructuredData
        type="breadcrumb"
        data={{
          items: [
            { name: "Home", url: "https://larryshelp.org" },
            { name: "Personal Forecast", url: "https://larryshelp.org/forecast" }
          ]
        }}
      />

      {/* Header */}
      <div className="forecast-header">
        <h1>Personal Forecast</h1>
        <p>Your complete financial picture — accounts, cash flows, and projections</p>
      </div>

      {/* A draft that lost to a newer saved copy is offered, never discarded silently. */}
      {shelvedDraft && (
        <div style={{ padding: '0.8rem 1rem', background: '#fef3c7', borderRadius: '8px',
          marginBottom: '1rem', color: '#92400e', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <span>
            This forecast was saved somewhere else since you last edited it, so the saved version is
            showing. Your unsaved changes from this tab are still here if you want them.
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="btn-secondary" style={{ padding: '4px 12px' }}
              onClick={() => {
                const d = shelvedDraft;
                TylerState.forecast.scenarioId = d.scenarioId || null;
                TylerState.forecast.scenarioName = d.scenarioName || 'My Forecast';
                TylerState.forecast.accounts = d.accounts || [];
                TylerState.forecast.cashFlows = d.cashFlows || [];
                TylerState.forecast.fxRates = d.fxRates || [];
                Object.assign(TylerState.forecast.settings, d.settings || {});
                TylerState.forecast.isDirty = true;
                setShelvedDraft(null);
                setSaveStatus('Your unsaved changes are back. Saving will overwrite the other copy.');
                updateReact();
              }}>
              Use my unsaved changes
            </button>
            <button className="btn-secondary" style={{ padding: '4px 12px' }}
              onClick={() => {
                try { sessionStorage.removeItem('larryshelp_forecast_draft'); } catch { /* ignore */ }
                setShelvedDraft(null);
              }}>
              Discard them
            </button>
          </div>
        </div>
      )}

      {/* Section Nav */}
      <div className="results-tabs" style={{ marginBottom: '1.5rem' }}>
        {visibleSections.map(s => (
          <button
            key={s}
            className={`tab ${activeSection === s ? 'active' : ''}`}
            onClick={() => setSection(s)}
          >
            {SECTION_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Save bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <input
          type="text"
          value={forecast.scenarioName}
          onChange={e => { forecast.scenarioName = e.target.value; forecast.isDirty = true; updateReact(); }}
          style={{ fontSize: '1.1rem', fontWeight: 600, border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 12px', width: '300px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {saveStatus && <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{saveStatus}</span>}
          <button
            className="btn-primary"
            onClick={saveScenario}
            disabled={!forecast.isDirty && Boolean(forecast.scenarioId)}
            style={{ opacity: forecast.isDirty || !forecast.scenarioId ? 1 : 0.5 }}
          >
            {forecast.isDirty || !forecast.scenarioId ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Section Content */}
      <div className="wizard-content">
        {activeSection === 'accounts' && <AccountsManager />}

        {activeSection === 'cashFlows' && <CashFlowsManager />}

        {activeSection === 'fxRates' && <FXManager />}

        {activeSection === 'forecast' && <ForecastResults />}

        {activeSection === 'settings' && (
          <div className="settings-panel">
            <h2>Settings</h2>

            <div className="setting-group">
              <label>View Mode</label>
              <select value={settings.viewMode} onChange={e => updateSetting('viewMode', e.target.value as ViewMode)}>
                <option value="simple">Simple</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
              <p className="help-text">
                {settings.viewMode === 'simple' && 'Minimal details — just the essentials'}
                {settings.viewMode === 'advanced' && 'More details and customization options'}
                {settings.viewMode === 'expert' && 'Full control over all calculations'}
              </p>
            </div>

            <div className="setting-group">
              <label>Base Currency</label>
              <select value={settings.baseCurrency} onChange={e => updateSetting('baseCurrency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="setting-group">
              <label>Forecast Length</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="radio"
                    name="horizon-kind"
                    checked={settings.horizon !== 'retirement'}
                    onChange={() => { forecast.settings.horizon = 'custom'; forecast.settings.customMonths = horizonYears * 12; forecast.isDirty = true; updateReact(); }}
                  />
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={horizonYears}
                    disabled={settings.horizon === 'retirement'}
                    onChange={e => {
                      const years = Math.min(100, Math.max(1, parseInt(e.target.value) || 1));
                      forecast.settings.horizon = 'custom';
                      forecast.settings.customMonths = years * 12;
                      forecast.isDirty = true;
                      updateReact();
                    }}
                    style={{ width: '80px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right' }}
                  />
                  years
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="radio"
                    name="horizon-kind"
                    checked={settings.horizon === 'retirement'}
                    onChange={() => updateSetting('horizon', 'retirement' as TimelineHorizon)}
                  />
                  until retirement
                  {settings.horizon === 'retirement' && (
                    <input
                      type="month"
                      value={settings.retirementDate}
                      onChange={e => updateSetting('retirementDate', e.target.value)}
                      style={{ marginLeft: '6px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                    />
                  )}
                </label>
              </div>
              <p className="help-text">Any length from 1 to 100 years, counted from the month after your latest recorded balance. Long forecasts open in the Annual view.</p>
            </div>

            <div className="setting-group">
              <label>Categories</label>
              <p className="help-text" style={{ marginTop: 0 }}>
                Every category your cash flows can use. The ones that come with the app are just
                names - rename or remove any of them, and add your own for a rental or side business.
                Each reports as a single netted line on the forecast with its own schedule below.
              </p>
              {categories.map((name, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                  <input
                    value={name}
                    onChange={e => renameCategory(i, e.target.value)}
                    placeholder="95 McMahon Dr"
                    style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', width: '220px' }}
                  />
                  <button
                    className="btn-secondary"
                    onClick={() => removeCategory(i)}
                    title={usageCount(name) > 0
                      ? `${usageCount(name)} cash flow item(s) use this`
                      : 'Nothing uses this'}
                    style={{ padding: '4px 10px' }}
                  >
                    Delete{usageCount(name) > 0 ? ` (${usageCount(name)} in use)` : ''}
                  </button>
                </div>
              ))}
              <button
                className="btn-secondary"
                onClick={() => updateSetting('categories', [...categories, ''])}
                style={{ padding: '4px 10px' }}
              >
                Add a category
              </button>
            </div>

            <div className="setting-group">
              <label>Birth Year</label>
              <input
                type="number"
                value={settings.birthYear || ''}
                onChange={e => updateSetting('birthYear', parseInt(e.target.value, 10) || 0)}
                placeholder="1987"
                style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', width: '100px' }}
              />
              <p className="help-text">Labels every year with the age you reach in it, so you can line up a forecast against turning 60 or 65. Leave blank to hide ages.</p>
            </div>

            <div className="setting-group">
              <label>Inflation Rate (%)</label>
              <input
                type="number"
                value={settings.inflationRate}
                onChange={e => updateSetting('inflationRate', parseFloat(e.target.value) || 0)}
                step="0.1"
                style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', width: '100px' }}
              />
            </div>

            <div className="setting-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.showFX}
                  onChange={e => updateSetting('showFX', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Enable Multi-Currency (FX Rates)
              </label>
            </div>

            <div className="setting-group">
              <label>Valuation</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(['nominal', 'real'] as ValuationMode[]).map(m => (
                  <button
                    key={m}
                    className={settings.valuationMode === m ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => updateSetting('valuationMode', m)}
                    style={{ padding: '6px 16px' }}
                  >
                    {m === 'nominal' ? 'Nominal $' : 'Real $ (inflation-adjusted)'}
                  </button>
                ))}
              </div>
            </div>

            <div className="setting-actions" style={{ marginTop: '1.5rem' }}>
              <button className="btn-primary" onClick={saveScenario}>
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalForecast;
